import {loggerFactory} from "@cm/browser-log"
import {AnyImageOpHal, ImageOpHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal"
import {ImageOpImgProc} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/imgproc/image-op-imgproc"
import {ImageOpContextHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-hal"
import {ImageOpContextImgProc} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-imgproc"
import {traverse} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/utils/image-op-utils"
import {GraphNode} from "@cm/graph-v3"
import {applyParameterDefaults} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/apply-parameter-defaults"
import {deepEqual, REF_COUNT_DEBUG_ID_INITIAL, TrackedRefCountable} from "@cm/utils"
import {merge, Subject} from "rxjs"
import {z} from "zod/v4-mini"
const ENABLE_DEBUG_INFO = true

export const DisableImageOpNodeZodValidation = true // set to true to disable zod validation in the texture editor, would be useful for speedup, but doesn't work yet
export const DisableImageOpNodeZodValidationInGraphTraversal = true // set to true to disable zod validation in the texture editor, would be useful for speedup, but doesn't work yet

export type AnyImageOpContext = ImageOpContextHal | ImageOpContextImgProc

export const isAnyImageOpResult = (value: unknown): value is AnyImageOpResult => {
    return value instanceof ImageOpHal || value instanceof ImageOpImgProc
}
export const anyImageOpResult = z.union([z.instanceof(ImageOpHal), z.instanceof(ImageOpImgProc)])
export type AnyImageOpResult = AnyImageOpHal | ImageOpImgProc

export type ImageOpDefinition<ReturnType = unknown> = {
    validation: {
        returns: z.ZodMiniType<ReturnType>
        parameters: z.ZodMiniType<{[key: string]: unknown}, {[key: string]: unknown}>
    }
}

export type AnyImageOpGraphNode = GraphNode<unknown, AnyImageOpContext>

export type ImageOpNodeReturning<ReturnType> = ImageOpNode<ImageOpDefinition<ReturnType>>

export type AnyImageOpResultNode = ImageOpNodeReturning<AnyImageOpResult>
export type AnyImageOpResultOrNode = ValueOrNode<AnyImageOpResult>

// nominal (class) wrapper around @cm/hal's NativeImageData so it can be added to SkipMappingTypes
// below alongside Float32Array/Uint32Array: those are also classes, and a plain object type there
// (rather than a class) makes ValueOrNodeRecursive's generic conditional resolve very differently
// for unrelated, still-generic node definitions, causing "type instantiation is excessively deep"
// errors far from this file.
export class NativeImageSource {
    constructor(
        readonly isSrgb: boolean,
        readonly data: HTMLImageElement | HTMLCanvasElement | ImageBitmap | ImageData,
    ) {}
}

export type SkipMappingTypes = Float32Array | Uint32Array | NativeImageSource

export type ValueOrNode<T> = T | GraphNode<T, AnyImageOpContext>
export type ValueOrNodeRecursive<T> = T extends AnyImageOpGraphNode
    ? T
    : T extends SkipMappingTypes
      ? ValueOrNode<T>
      : T extends object
        ? {
              [K in keyof T]: ValueOrNode<T[K]> | ValueOrNodeRecursive<T[K]>
          }
        : ValueOrNode<T>

// graph-v3's EvaluatedGraphNode/EvaluatedParameters only unwrap nodes of exactly the given Context; all image-op nodes are evaluated against the same context, so unwrap regardless of it
export type EvaluatedImageOpValue<T> = T extends GraphNode<infer Returns, never> ? Returns : T
export type EvaluatedImageOpParameters<Parameters> = {[P in keyof Parameters]: EvaluatedImageOpValue<Parameters[P]>}

export type EvaluatedValues<T, Context = unknown> = T extends AnyImageOpGraphNode
    ? T
    : T extends object
      ? {
            [P in keyof T]: EvaluatedValues<T[P], Context>
        }
      : EvaluatedImageOpValue<T>

export type ImageOpEvaluatedParameters<Definition extends ImageOpDefinition> = EvaluatedImageOpParameters<ImageOpParameters<Definition>>

export type ImageOpInputs<Definition extends ImageOpDefinition> = z.input<Definition["validation"]["parameters"]>
export type ImageOpParameters<Definition extends ImageOpDefinition> = z.output<Definition["validation"]["parameters"]>
export type ImageOpOutputs<Definition extends ImageOpDefinition> = z.output<Definition["validation"]["returns"]>
export type ImageOpInputsOrNodes<Definition extends ImageOpDefinition> = ValueOrNodeRecursive<ImageOpInputs<Definition>>
export type ImageOpParametersOrNodes<Definition extends ImageOpDefinition> = ValueOrNodeRecursive<ImageOpParameters<Definition>>
export type ImageOpOutputsOrNodes<Definition extends ImageOpDefinition> =
    | ValueOrNode<ImageOpOutputs<Definition>>
    | ValueOrNodeRecursive<ImageOpOutputs<Definition>>

export abstract class ImageOpNode<out Definition extends ImageOpDefinition = ImageOpDefinition>
    extends GraphNode<ImageOpOutputs<Definition>, AnyImageOpContext, ImageOpParameters<Definition>, ImageOpInputs<Definition>>
    implements TrackedRefCountable
{
    constructor(parameters: ImageOpInputsOrNodes<Definition>) {
        super({parameters: parameters as unknown as ImageOpParameters<Definition>, disableValidation: true})
        this.applySchemaDefaults()
        this._refCountsByDebugId.set(REF_COUNT_DEBUG_ID_INITIAL, 1)
        ImageOpNode.addInstance(this)
        this.setParameters(parameters) // we call setParameters here because unfortunately the super constructor doesn't call replaceParameters (TODO should this be changed in the base ?)
    }

    // the construction-time validation which used to fill in the schema defaults is skipped, so apply just those (setParameters merges over them)
    private applySchemaDefaults() {
        this.parameters = applyParameterDefaults(this.definition.validation.parameters, this.parameters)
    }

    protected dispose() {
        // release referenced ImageOpNodes
        this._inputImageOpNodes.forEach((imageOpNode) => queueMicrotask(() => imageOpNode.release(REF_COUNT_DEBUG_ID_SOURCE_PARAMETER_NODE))) // use queueMicrotask to avoid stack-overflow due to deep call-stacks
        this._inputImageOpNodes.clear()
        ImageOpNode.removeInstance(this)
    }

    protected checkIsDisposed() {
        if (this._refCount <= 0) {
            ImageOpNode.logger.error(`ImageOpNode: operation called on disposed node ${this.nodeClass}`)
        }
    }

    set debugId(debugId: string | undefined) {
        this._debugId = debugId
    }

    get debugId() {
        return this._debugId
    }

    get refCount(): number {
        return this._refCount
    }

    addRef(debugId: string) {
        if (this._refCount <= 0) {
            throw new Error("RefCountableBase: addRef called on a disposed object")
        }
        ++this._refCount
        const count = this._refCountsByDebugId.get(debugId) ?? 0
        this._refCountsByDebugId.set(debugId, count + 1)
        return this
    }

    release(debugId: string) {
        if (this._refCount <= 0) {
            throw new Error("RefCountableBase: release called on a disposed object")
        }
        --this._refCount
        const count = this._refCountsByDebugId.get(debugId)
        if (count == null || count === 0) {
            throw new Error(`RefCountableBase: release() called with debugId "${debugId}" that was not found in ref counts map`)
        } else if (count === 1) {
            this._refCountsByDebugId.delete(debugId)
        } else {
            this._refCountsByDebugId.set(debugId, count - 1)
        }
        if (this._refCount === 0) {
            this.dispose()
        }
    }

    listenToParameterChange(...parameterNames: (keyof ImageOpParameters<Definition>)[]) {
        const parameterChanges = parameterNames.map((parameterName) => {
            let parameterChange = this._parameterChangeByName[parameterName]
            if (!parameterChange) {
                parameterChange = new Subject<void>()
                this._parameterChangeByName[parameterName] = parameterChange
            }
            return parameterChange
        })
        return merge(...parameterChanges)
    }

    // public setParameter = <Name extends string>(name: Name, value: unknown) => {
    //     const schema = this.definition.validation.parameters.shape[name as string]
    //     if (!schema) {
    //         throw new Error(`Failed to find schema for setting ${name} (node: ${this.nodeClass})`)
    //     }
    //     const parsedValue = schema.parse(value)
    //     this.updateParameters({
    //         ...this.parameters,
    //         [name]: parsedValue,
    //     } as Partial<GetParameters<Definition>>)
    // }

    setParameters(parameters: ImageOpInputsOrNodes<Definition>) {
        let parsedValue: ImageOpParameters<Definition>
        if (DisableImageOpNodeZodValidation) {
            parsedValue = parameters as unknown as ImageOpParameters<Definition>
        } else {
            parsedValue = this.ensureValidParameters(parameters as unknown as ImageOpInputs<Definition>)
        }
        this.updateParameters(parsedValue as Partial<ImageOpParameters<Definition>>, DisableImageOpNodeZodValidation)
    }

    override replaceParameters(newParameters: ImageOpParameters<Definition>, disableValidation: true): void
    override replaceParameters(newParameters: ImageOpInputs<Definition>, disableValidation?: false): void
    override replaceParameters(newParameters: ImageOpParameters<Definition> | ImageOpInputs<Definition>, disableValidation = false): void {
        // collect new ImageOpNodes
        const newImageOpNodes = new Set<ImageOpNode>()
        traverse(newParameters, (value) => {
            if (value instanceof ImageOpNode) {
                newImageOpNodes.add(value)
            }
        })

        // addRef new ImageOpNodes
        newImageOpNodes.forEach((imageOpNode) => imageOpNode.addRef(REF_COUNT_DEBUG_ID_SOURCE_PARAMETER_NODE))

        // release old ImageOpNodes
        this._inputImageOpNodes.forEach((imageOpNode) => imageOpNode.release(REF_COUNT_DEBUG_ID_SOURCE_PARAMETER_NODE))

        // store new ImageOpNodes
        this._inputImageOpNodes = newImageOpNodes

        const changedParameters = Object.keys(newParameters).filter((key) => !deepEqual(newParameters[key], this.parameters[key]))
        if (disableValidation) {
            super.replaceParameters(newParameters as ImageOpParameters<Definition>, true)
        } else {
            super.replaceParameters(newParameters as ImageOpInputs<Definition>, false)
        }
        changedParameters.forEach((parameter) => this._parameterChangeByName[parameter]?.next())
    }

    private static addInstance(instance: ImageOpNode) {
        if (ENABLE_DEBUG_INFO) {
            ImageOpNode.instances.add(instance)
            ImageOpNode.logger.trace(`Creating ImageOpNode ${instance.constructor.name}`, instance)
        }
    }

    private static removeInstance(instance: ImageOpNode) {
        if (ENABLE_DEBUG_INFO) {
            ImageOpNode.instances.delete(instance)
            ImageOpNode.logger.trace(`Disposing ImageOpNode ${instance.debugId}`, instance)
        }
    }

    static printDebugInfo() {
        ImageOpNode.logger.log(`=== ${ImageOpNode.instances.size} ImageOpNode Instances ===`)
        ImageOpNode.instances.forEach((instance) => {
            ImageOpNode.logger.log(
                `  *** ${instance.debugId ?? "unnamed"} (RefCount: ${instance.refCount}) ***`,
                instance,
                Array.from(instance._refCountsByDebugId.entries()),
            )
        })
    }

    private _debugId?: string
    private _parameterChangeByName: Partial<Record<keyof typeof this.parameters, Subject<void>>> = {}
    private _inputImageOpNodes = new Set<ImageOpNode>()
    private _refCount = 1
    private readonly _refCountsByDebugId = new Map<string, number>()

    static instances = new Set<ImageOpNode>()
    // TODO: remove static logger, replace with instance logger
    protected static readonly logger = loggerFactory.getLogger("image-op-system")
}

const REF_COUNT_DEBUG_ID_SOURCE_PARAMETER_NODE = "ImageOpNode.sourceParameterNode"
